import { useState, useCallback } from 'react';
import ngeohash from 'ngeohash';

export interface ReverseGeocodeResult {
  formattedAddress: string;
  locationName: string;
  city?: string;
  country?: string;
  latitude: number;
  longitude: number;
  geohash: string;
}

interface UseReverseGeocodeReturn {
  reverseGeocode: (lat: number, lng: number) => Promise<ReverseGeocodeResult | null>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for reverse geocoding coordinates to addresses using Google Geocoding API
 *
 * @returns {UseReverseGeocodeReturn} Reverse geocoding methods and state
 *
 * @example
 * const { reverseGeocode, loading, error } = useReverseGeocode();
 *
 * const result = await reverseGeocode(43.5528, 7.0174);
 * if (result) {
 *   console.log('Address:', result.formattedAddress);
 *   console.log('Place name:', result.locationName);
 * }
 */
export const useReverseGeocode = (): UseReverseGeocodeReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reverseGeocode = useCallback(async (
    lat: number,
    lng: number
  ): Promise<ReverseGeocodeResult | null> => {
    // Check if Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps) {
      setError('Google Maps API not loaded');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const geocoder = new google.maps.Geocoder();

      const response = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      const result = response[0];

      // Extract location components
      const locationName = extractLocationName(result);
      const city = extractComponent(result, 'locality') || extractComponent(result, 'administrative_area_level_2');
      const country = extractComponent(result, 'country');

      // Generate geohash (precision 9 for ~4.77m accuracy)
      const geohash = ngeohash.encode(lat, lng, 9);

      setLoading(false);
      return {
        formattedAddress: result.formatted_address,
        locationName,
        city,
        country,
        latitude: lat,
        longitude: lng,
        geohash,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get address';
      console.error('Reverse geocoding error:', err);
      setError(message);
      setLoading(false);
      return null;
    }
  }, []);

  return { reverseGeocode, loading, error };
};

/**
 * Extract the most appropriate location name from geocoder result
 * Priority: POI > establishment > route (with street number) > neighborhood > locality
 */
const extractLocationName = (result: google.maps.GeocoderResult): string => {
  const priorityTypes = [
    'point_of_interest',
    'establishment',
    'premise',
    'subpremise',
    'route',
    'neighborhood',
    'sublocality_level_1',
    'sublocality',
    'locality'
  ];

  for (const type of priorityTypes) {
    const component = result.address_components.find(c => c.types.includes(type));
    if (component) {
      // For route (street name), include the street number if available
      if (type === 'route') {
        const streetNumber = result.address_components.find(c => c.types.includes('street_number'));
        if (streetNumber) {
          return `${streetNumber.long_name} ${component.long_name}`;
        }
      }
      return component.long_name;
    }
  }

  // Fallback: first part of formatted address
  return result.formatted_address.split(',')[0].trim();
};

/**
 * Extract a specific address component by type
 */
const extractComponent = (
  result: google.maps.GeocoderResult,
  type: string
): string | undefined => {
  return result.address_components.find(c => c.types.includes(type))?.long_name;
};

export default useReverseGeocode;
